// Data hook for the admin-gated `/admin/code-map` provisioning surface.
// Mirrors useAccess.ts's error/403 idiom, but there's no fetch-on-mount:
// there's no single project until one is picked in the page's project
// picker, so `fetchStatus`/`generate` are both caller-invoked against a
// chosen `projectId`.

import { useCallback, useState } from "react";

import {
  AdminCodeMapGenerateResponseSchema,
  AdminCodeMapStatusSchema,
  type AdminCodeMapGenerateResponse,
  type AdminCodeMapStatus,
} from "@loom/shared/admin";

import { HttpError, UnauthorizedError, httpGet, httpPost } from "../lib/http";

/**
 * Turn a failed request into one line an operator can act on. The server's
 * error bodies are `{ error, detail? }`; `detail` is the useful part (it names
 * the upstream call that failed) and `error` is the code. Falls back to the
 * raw text only when the body isn't the shape we expect — never surfaces the
 * `"502 : {…}"` debug string, which leaks JSON and internal API paths.
 */
const describeFailure = (err: unknown): string => {
  const body = err instanceof HttpError ? err.body : undefined;
  if (body) {
    try {
      const parsed: unknown = JSON.parse(body);
      if (parsed && typeof parsed === "object") {
        const { detail, error } = parsed as {
          detail?: unknown;
          error?: unknown;
        };
        if (typeof detail === "string" && detail.trim()) return detail.trim();
        if (typeof error === "string" && error.trim()) return error.trim();
      }
    } catch {
      // Not JSON — fall through to the raw text.
    }
    return body.trim();
  }
  return err instanceof Error ? err.message : String(err);
};

/**
 * Recognise the one failure an operator hits by accident: picking a project
 * with no readable repository. The upstream reason ("GitLab GET
 * /…/repository/tree returned HTTP 404") is accurate but reads like a stack
 * trace, so the page prepends a plain sentence. Deliberately narrow — an
 * unrecognised failure keeps its raw reason rather than being softened into
 * something vague.
 */
const isMissingRepo = (detail: string): boolean =>
  /repository\/tree/.test(detail) && /\b404\b/.test(detail);

/** Which request failed — the two have different copy. */
export type CodeMapErrorKind = "status" | "generate";

export interface CodeMapAdminState {
  readonly status: AdminCodeMapStatus | null;
  readonly loading: boolean;
  readonly generating: boolean;
  readonly error: string | null;
  /** `null` whenever `error` is null. */
  readonly errorKind: CodeMapErrorKind | null;
  /** Set when the failure was recognised as "project has no repository", so
   *  the page can lead with a plain sentence before the raw reason. */
  readonly errorNoRepo: boolean;
  /** The server 403'd — caller is not an administrator. */
  readonly forbidden: boolean;
  /** GET the current provisioning status for a project. */
  readonly fetchStatus: (projectId: number) => Promise<void>;
  /** POST /generate for a project, then leaves the caller to re-fetch status. */
  readonly generate: (projectId: number) => Promise<AdminCodeMapGenerateResponse | null>;
}

export const useCodeMapAdmin = (): CodeMapAdminState => {
  const [status, setStatus] = useState<AdminCodeMapStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<CodeMapErrorKind | null>(null);
  const [errorNoRepo, setErrorNoRepo] = useState<boolean>(false);
  const [forbidden, setForbidden] = useState<boolean>(false);

  const fail = useCallback((kind: CodeMapErrorKind, err: unknown): void => {
    const detail = describeFailure(err);
    setError(detail);
    setErrorKind(kind);
    setErrorNoRepo(isMissingRepo(detail));
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
    setErrorKind(null);
    setErrorNoRepo(false);
  }, []);

  const fetchStatus = useCallback(async (projectId: number): Promise<void> => {
    setLoading(true);
    clearError();
    setForbidden(false);
    try {
      const value = await httpGet(
        `/api/admin/code-map/${projectId}`,
        AdminCodeMapStatusSchema,
      );
      setStatus(value);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        // Route loader redirects to /sign-in; keep loading, don't flash.
        return;
      }
      if (err instanceof HttpError && err.status === 403) {
        setForbidden(true);
        return;
      }
      fail("status", err);
    } finally {
      setLoading(false);
    }
  }, [clearError, fail]);

  const generate = useCallback(
    async (projectId: number): Promise<AdminCodeMapGenerateResponse | null> => {
      setGenerating(true);
      clearError();
      try {
        const value = await httpPost(
          `/api/admin/code-map/${projectId}/generate`,
          {},
          AdminCodeMapGenerateResponseSchema,
        );
        return value;
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          return null;
        }
        if (err instanceof HttpError && err.status === 403) {
          setForbidden(true);
          return null;
        }
        fail("generate", err);
        return null;
      } finally {
        setGenerating(false);
      }
    },
    [clearError, fail],
  );

  return {
    status,
    loading,
    generating,
    error,
    errorKind,
    errorNoRepo,
    forbidden,
    fetchStatus,
    generate,
  };
};
